import React, { useState, useEffect, useRef, useCallback } from "react";
import ParticipantDialogFrame from '../Dialogs/ParticipantDialogFrame';
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import {
    Box,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Avatar,
    Badge,
    Typography,
    Chip,
    Divider,
    InputBase,
    alpha,
    Button,
    Paper,
    Tabs,
    Tab,
    IconButton,
    Menu,
    MenuItem,
    DialogContent,
    DialogContentText,
    DialogActions,
} from "@mui/material";
import {
    Search as SearchIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    PersonAdd as PersonAddIcon,
    MoreVert as MoreVertIcon,
    DeleteOutline as DeleteOutlineIcon,
    NotificationsOff as NotificationsOffIcon,
    Groups as GroupsIcon,
} from "@mui/icons-material";
import {
    IConnection,
    IUser,
} from "../../interfaces/IChatUser";
import { IPodResponse } from "../../interfaces/ICommunityHub";
import { useApiClient } from "../../ApiClientProvider";
import { ConnectionService } from "../../services/connectionService";
import { PodService } from "../../services/podService";
import { getAvatarGradient } from "../../helpers/avatarColor";
import { useNotifications } from "@toolpad/core";

function formatRelativeTime(isoString?: string | null): string {
    if (!isoString) return "";
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return "";

    const diffMs = Date.now() - date.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    if (diffMinutes < 1) return "now";
    if (diffMinutes < 60) return `${diffMinutes}m`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d`;
}

function toTitleCase(value: string): string {
    return value.replace(/\b\w/g, (char) => char.toUpperCase());
}

function renderPodPreviewText(pod: IPodResponse): React.ReactNode {
    const preview = pod.lastMessagePreview ?? "No messages yet";
    const podName = pod.name?.trim();

    if (!podName) {
        return preview;
    }

    const isJoinLeaveSystemMessage = /\b(joined|left)\b/i.test(preview);
    if (!isJoinLeaveSystemMessage) {
        return preview;
    }

    const lowerPreview = preview.toLowerCase();
    const lowerPodName = podName.toLowerCase();
    const podNameIndex = lowerPreview.lastIndexOf(lowerPodName);

    if (podNameIndex < 0) {
        return preview;
    }

    const before = preview.slice(0, podNameIndex);
    const matched = preview.slice(podNameIndex, podNameIndex + podName.length);
    const after = preview.slice(podNameIndex + podName.length);

    return (
        <>
            {before}
            <Box component="span" sx={{ fontWeight: 600 }}>
                {toTitleCase(matched)}
            </Box>
            {after}
        </>
    );
}

interface ChatConnectionsProps {
    onStartChat: (user: IUser) => void;
    onAcceptRequest: (requestId: string) => void;
    onRejectRequest: (requestId: string) => void;
    onCancelRequest: (requestId: string) => void;
    onOpenPodChat?: (podId: string) => void;
}

const ChatConnections: React.FC<ChatConnectionsProps> = ({
    onStartChat,
    onAcceptRequest,
    onRejectRequest,
    onCancelRequest,
    onOpenPodChat,
}) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [connections, setConnections] = useState<IConnection[]>([]);
    const [filteredConnections, setFilteredConnections] = useState<IConnection[]>(
        []
    );
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [pods, setPods] = useState<IPodResponse[]>([]);
    const apiClient = useApiClient('applicationClient');

    // ✅ Memoize connectionService to avoid recreating on every render
    const connectionService = React.useMemo(() => new ConnectionService(apiClient), [apiClient]);
    const podService = React.useMemo(() => new PodService(apiClient), [apiClient]);

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const scrollPositionRef = useRef<number>(0);
    const longPressTimerRef = useRef<number | null>(null);

    // ✅ Get state directly from Redux store
    const unreadCounts = useSelector((state: RootState) => state.chat.unreadCounts);
    const connectionRequests = useSelector((state: RootState) => state.chat.connectionRequests);
    const onlineStatus = useSelector((state: RootState) => state.chat.onlineStatus);
    const connectionsVersion = useSelector((state: RootState) => (state.chat as any).connectionsVersion);
    const podUnreadCounts = useSelector((state: RootState) => state.pods.podUnreadCounts);

    // Context menu state
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
    const [menuConnection, setMenuConnection] = useState<IConnection | null>(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [processingRequests, setProcessingRequests] = useState<Set<string>>(new Set());

    const notifications = useNotifications();

    // Wrap handlers to track processing state
    const wrappedAcceptRequest = useCallback((requestId: string) => {
        setProcessingRequests(prev => new Set(prev).add(requestId));
        onAcceptRequest(requestId);
        // Note: useChatSystem will clear this via its finally block
        setTimeout(() => setProcessingRequests(prev => {
            const next = new Set(prev);
            next.delete(requestId);
            return next;
        }), 5000); // safety timeout
    }, [onAcceptRequest]);

    const wrappedRejectRequest = useCallback((requestId: string) => {
        setProcessingRequests(prev => new Set(prev).add(requestId));
        onRejectRequest(requestId);
        setTimeout(() => setProcessingRequests(prev => {
            const next = new Set(prev);
            next.delete(requestId);
            return next;
        }), 5000);
    }, [onRejectRequest]);

    const wrappedCancelRequest = useCallback((requestId: string) => {
        setProcessingRequests(prev => new Set(prev).add(requestId));
        onCancelRequest(requestId);
        setTimeout(() => setProcessingRequests(prev => {
            const next = new Set(prev);
            next.delete(requestId);
            return next;
        }), 5000);
    }, [onCancelRequest]);

    // 🔹 Fetch connections (Accepted + Pending)
    const fetchConnections = useCallback(async () => {
        try {
            setLoading(true);

            // Accepted connections
            const acceptedRes = await connectionService.getAll({
                filters: { Status: "Accepted" },
            });

            // Normalize accepted connections
            setConnections(
                (acceptedRes.items as any[]).map((raw) => ({
                    id: raw.otherUserId,
                    status: raw.status,
                    message: raw.message,
                    timestamp: raw.requestedAt ?? raw.respondedAt,
                    user: {
                        id: raw.otherUserId,
                        name: raw.otherUserName ?? "Unknown",
                        avatar: raw.otherUserImage ?? "",
                        isOnline: raw.isOnline ?? false,
                        lastSeen: raw.lastSeen ?? "",
                    },
                }))
            );
        } catch (err) {
            console.error("Failed to fetch connections:", err);
        } finally {
            setLoading(false);
        }
    }, [connectionService]);

    useEffect(() => {
        console.log(`🔄 Fetching connections (version: ${connectionsVersion})`);
        fetchConnections();
    }, [fetchConnections, connectionsVersion]);

    // Load pods for the Pods tab (group chats)
    useEffect(() => {
        let isActive = true;

        const loadPods = async () => {
            try {
                const data = await podService.getPods();
                if (!isActive) return;
                const sorted = [...data].sort((a, b) => {
                    const aTime = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
                    const bTime = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
                    return bTime - aTime;
                });
                setPods(sorted);
            } catch (err) {
                console.error("Failed to load pods:", err);
            }
        };

        void loadPods();

        return () => { isActive = false; };
    }, [podService]);

    // ✅ Refresh connections when requests change (accept/reject)
    useEffect(() => {
        // Refetch connections when connection requests list changes or version bumps
        if (activeTab === 0) {
            console.log(`🔄 Refetching connections on tab 0 (requests: ${connectionRequests.length}, version: ${connectionsVersion})`);
            fetchConnections();
        }
    }, [connectionRequests.length, activeTab, fetchConnections, connectionsVersion]);

    // 🔹 Merge connections with real-time online status from Redux
    const connectionsWithOnlineStatus = React.useMemo(() => {
        return connections.map(conn => ({
            ...conn,
            user: {
                ...conn.user,
                // Override with Redux online status if available
                isOnline: onlineStatus[conn.user.id] ?? conn.user.isOnline ?? false,
            }
        }));
    }, [connections, onlineStatus]);

    // 🔹 Filter search
    useEffect(() => {
        const filtered = connectionsWithOnlineStatus.filter((connection) =>
            connection.user?.name
                ?.toLowerCase()
                .includes(searchTerm.toLowerCase())
        );
        setFilteredConnections(filtered);
    }, [connectionsWithOnlineStatus, searchTerm]);

    const onlineCount = connectionsWithOnlineStatus.filter((c) => c.user?.isOnline).length;

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    const handleStartChat = useCallback(
        (user: IUser) => {
            if (!user) return;

            if (scrollContainerRef.current) {
                scrollPositionRef.current = scrollContainerRef.current.scrollTop;
            }

            onStartChat(user);

            requestAnimationFrame(() => {
                if (scrollContainerRef.current && scrollPositionRef.current > 0) {
                    scrollContainerRef.current.scrollTop =
                        scrollPositionRef.current;
                }
            });
        },
        [onStartChat]
    );

    // Open the context menu for a specific connection
    const openMenu = useCallback((anchorEl: HTMLElement, connection: IConnection) => {
        setMenuAnchorEl(anchorEl);
        setMenuConnection(connection);
    }, []);

    const closeMenu = useCallback(() => {
        setMenuAnchorEl(null);
    }, []);

    const handleLongPressStart = useCallback((target: HTMLElement, connection: IConnection) => {
        // Long-press after ~600ms to open menu (mobile-friendly)
        if (longPressTimerRef.current) {
            window.clearTimeout(longPressTimerRef.current);
        }
        longPressTimerRef.current = window.setTimeout(() => {
            openMenu(target, connection);
        }, 600);
    }, [openMenu]);

    const cancelLongPress = useCallback(() => {
        if (longPressTimerRef.current) {
            window.clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
    }, []);

    const requestRemoveConnection = useCallback(() => {
        closeMenu();
        setConfirmOpen(true);
    }, [closeMenu]);

    const handleConfirmRemove = useCallback(async () => {
        if (!menuConnection) return;
        try {
            setRemoving(true);
            console.log(`🗑️ Removing connection with user ${menuConnection.user.id}`);
            await connectionService.remove(menuConnection.user.id);
            // Optimistically update local list
            setConnections(prev => prev.filter(c => c.user?.id !== menuConnection.user.id));
            // Close any open chat window with this user
            if (window.store) {
                const state = window.store.getState();
                const chats = state.chat?.chats || [];
                chats.forEach((chat: any) => {
                    if (chat.participants.some((p: any) => p.id === menuConnection.user.id)) {
                        window.store.dispatch({ type: 'chat/removeChat', payload: chat.id });
                    }
                });
            }
            notifications.show(`Removed ${menuConnection.user.name} from your connections`, {
                severity: 'success',
                autoHideDuration: 3000,
            });
        } catch (err) {
            console.error("Failed to remove connection:", err);
            notifications.show(`Failed to remove connection: ${err instanceof Error ? err.message : 'Unknown error'}`, {
                severity: 'error',
                autoHideDuration: 3000,
            });
        } finally {
            setRemoving(false);
            setConfirmOpen(false);
            setMenuConnection(null);
        }
    }, [connectionService, menuConnection, notifications]);

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="fullWidth"
                    sx={{
                        minHeight: 40,
                        "& .MuiTab-root": {
                            minHeight: 40,
                            fontSize: "0.8rem",
                            textTransform: "none",
                            fontWeight: 500,
                        },
                    }}
                >
                    <Tab
                        label={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                Chats
                                <Chip
                                    label={connections.length}
                                    size="small"
                                    sx={{ height: 16, fontSize: "0.7rem" }}
                                />
                            </Box>
                        }
                    />
                    <Tab
                        label={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                Pods
                                {pods.length > 0 && (
                                    <Chip
                                        label={pods.length}
                                        size="small"
                                        sx={{ height: 16, fontSize: "0.7rem" }}
                                    />
                                )}
                            </Box>
                        }
                    />
                    <Tab
                        label={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                Requests
                                {connectionRequests.length > 0 && (
                                    <Chip
                                        label={connectionRequests.length}
                                        size="small"
                                        color="error"
                                        sx={{ height: 16, fontSize: "0.7rem" }}
                                    />
                                )}
                            </Box>
                        }
                    />
                </Tabs>
            </Box>

            {/* Tab Content */}
            {activeTab === 0 && (
                <>
                    {/* Search Bar */}
                    <Box sx={{ p: 2, pb: 1 }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                backgroundColor: alpha("#000", 0.04),
                                borderRadius: 2,
                                px: 2,
                                py: 1,
                                border: "1px solid",
                                borderColor: alpha("#000", 0.08),
                            }}
                        >
                            <SearchIcon
                                sx={{ color: "text.secondary", mr: 1, fontSize: 20 }}
                            />
                            <InputBase
                                placeholder="Search connections..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                sx={{ flex: 1, fontSize: "0.875rem" }}
                            />
                        </Box>
                    </Box>

                    {/* Online Status */}
                    <Box sx={{ px: 2, pb: 1 }}>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={500}
                        >
                            {onlineCount} of {connections.length} online
                        </Typography>
                    </Box>

                    {/* Connections List */}
                    <Box ref={scrollContainerRef} sx={{ flex: 1, overflow: "auto" }}>
                        <List sx={{ p: 0 }}>
                            {filteredConnections.map((connection, index) => {
                                const user = connection.user;
                                if (!user) return null;

                                const unreadCount = unreadCounts[user.id] || 0;

                                return (
                                    <React.Fragment key={connection.id}>
                                        <ListItem
                                            sx={{
                                                cursor: "pointer",
                                                "&:hover": {
                                                    backgroundColor: alpha("#000", 0.04),
                                                },
                                                py: 1.5,
                                                px: 2,
                                            }}
                                            onClick={() => handleStartChat(user)}
                                            onContextMenu={(e) => {
                                                e.preventDefault();
                                                openMenu(e.currentTarget as HTMLElement, connection);
                                            }}
                                            onTouchStart={(e) => handleLongPressStart(e.currentTarget as HTMLElement, connection)}
                                            onTouchEnd={cancelLongPress}
                                            onTouchMove={cancelLongPress}
                                            onTouchCancel={cancelLongPress}
                                        >
                                            <ListItemAvatar>
                                                <Badge
                                                    color={user.isOnline ? "success" : "default"}
                                                    variant="dot"
                                                    anchorOrigin={{
                                                        vertical: "bottom",
                                                        horizontal: "right",
                                                    }}
                                                    sx={{
                                                        "& .MuiBadge-dot": {
                                                            width: 12,
                                                            height: 12,
                                                            borderRadius: "50%",
                                                            border: "2px solid white",
                                                        },
                                                    }}
                                                >
                                                    <Avatar src={user.avatar} sx={{ width: 44, height: 44 }}>
                                                        {user.name.charAt(0)}
                                                    </Avatar>
                                                </Badge>
                                            </ListItemAvatar>

                                            <ListItemText
                                                primary={
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <Typography variant="body2" fontWeight={500}>
                                                            {user.name}
                                                        </Typography>
                                                        {unreadCount > 0 && (
                                                            <Chip
                                                                label={unreadCount}
                                                                size="small"
                                                                color="error"
                                                                sx={{ height: 18, fontSize: "0.75rem" }}
                                                            />
                                                        )}
                                                    </Box>
                                                }
                                                secondary={
                                                    user.isOnline ? (
                                                        <Typography
                                                            variant="caption"
                                                            fontWeight={600}
                                                            sx={{ fontSize: "0.8rem" }}
                                                        >
                                                            Online
                                                        </Typography>
                                                    ) : (
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{ fontSize: "0.7rem" }}
                                                        >
                                                            {user.lastSeen}
                                                        </Typography>
                                                    )
                                                }
                                            />

                                            {/* Actions */}
                                            <IconButton
                                                edge="end"
                                                size="small"
                                                aria-label="connection actions"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openMenu(e.currentTarget as HTMLElement, connection);
                                                }}
                                            >
                                                <MoreVertIcon />
                                            </IconButton>
                                        </ListItem>
                                        {index < filteredConnections.length - 1 && (
                                            <Divider variant="inset" component="li" sx={{ ml: 8 }} />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </List>

                        {!loading && filteredConnections.length === 0 && (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: 200,
                                    color: "text.secondary",
                                    px: 2,
                                }}
                            >
                                <Typography variant="body2" align="center">
                                    {searchTerm
                                        ? "No connections found"
                                        : "No connections available"}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </>
            )}

            {/* Pods Tab */}
            {activeTab === 1 && (
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                    <Box sx={{ flex: 1, overflow: "auto" }}>
                        {pods.length === 0 ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: 200,
                                    color: "text.secondary",
                                }}
                            >
                                <GroupsIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                                <Typography variant="body2" align="center">
                                    No pods yet
                                </Typography>
                            </Box>
                        ) : (
                            <List sx={{ p: 0 }}>
                                {pods.map((pod) => {
                                    const podUnreadCount = podUnreadCounts[pod.id] ?? pod.unreadMessageCount ?? 0;

                                    return (
                                    <React.Fragment key={pod.id}>
                                        <ListItem
                                            sx={{
                                                cursor: "pointer",
                                                "&:hover": {
                                                    backgroundColor: alpha("#000", 0.04),
                                                },
                                                py: 1.5,
                                                px: 2,
                                                alignItems: "flex-start",
                                            }}
                                            onClick={() => onOpenPodChat?.(pod.id)}
                                        >
                                            <ListItemAvatar>
                                                <Avatar
                                                    variant="rounded"
                                                    src={pod.imageUrl || undefined}
                                                    sx={{
                                                        width: 44,
                                                        height: 44,
                                                        borderRadius: "12px",
                                                        background: getAvatarGradient(pod.id || pod.name),
                                                        fontWeight: 600,
                                                        fontSize: "1.1rem",
                                                        color: "#fff",
                                                    }}
                                                >
                                                    {pod.name.charAt(0).toUpperCase()}
                                                </Avatar>
                                            </ListItemAvatar>

                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                                                        <Typography variant="body2" fontWeight={600} noWrap sx={{ textTransform: 'capitalize' }}>
                                                            {pod.name}
                                                        </Typography>
                                                        {pod.muted && (
                                                            <NotificationsOffIcon sx={{ fontSize: 14, color: "text.disabled", flexShrink: 0 }} />
                                                        )}
                                                        {podUnreadCount > 0 && (
                                                            <Chip
                                                                label={podUnreadCount}
                                                                size="small"
                                                                color="error"
                                                                sx={{ height: 18, fontSize: "0.75rem", flexShrink: 0 }}
                                                            />
                                                        )}
                                                    </Box>
                                                    {!!pod.lastMessageAt && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem", flexShrink: 0 }}>
                                                            {formatRelativeTime(pod.lastMessageAt)}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Typography
                                                    variant="caption"
                                                    color={podUnreadCount > 0 ? "text.primary" : "text.secondary"}
                                                    noWrap
                                                    sx={{ display: "block", fontSize: "0.78rem", mt: 0.25, fontWeight: podUnreadCount > 0 ? 500 : 400 }}
                                                >
                                                    {renderPodPreviewText(pod)}
                                                </Typography>
                                            </Box>
                                        </ListItem>
                                        <Divider variant="inset" sx={{ ml: 8 }} />
                                    </React.Fragment>
                                    );
                                })}
                            </List>
                        )}
                    </Box>
                    <Box sx={{ px: 2, py: 1.25, borderTop: "1px solid", borderColor: "divider", textAlign: "center", flexShrink: 0 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                            Pod group chats live in the same inbox as your 1:1 chats.
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* Requests Tab */}
            {activeTab === 2 && (
                <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
                    {connectionRequests.length === 0 ? (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                height: 200,
                                color: "text.secondary",
                            }}
                        >
                            <PersonAddIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                            <Typography variant="body2" align="center">
                                No connection requests
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            {connectionRequests.map((request) => {
                                // ✅ Use real-time online status from Redux
                                const isOnline = onlineStatus[request.from.id] ?? request.from.isOnline ?? false;

                                return (
                                    <Paper
                                        key={request.id}
                                        elevation={1}
                                        sx={{
                                            p: 2,
                                            backgroundColor: alpha("#3F51B5", 0.02),
                                            border: "1px solid",
                                            borderColor: alpha("#3F51B5", 0.1),
                                            borderRadius: 2,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 1.5,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1.5,
                                                }}
                                            >
                                                <Badge
                                                    overlap="circular"
                                                    anchorOrigin={{
                                                        vertical: "bottom",
                                                        horizontal: "right",
                                                    }}
                                                    variant="dot"
                                                    sx={{
                                                        "& .MuiBadge-dot": {
                                                            width: 10,
                                                            height: 10,
                                                            borderRadius: "50%",
                                                            border: "2px solid white",
                                                            backgroundColor: isOnline ? "#4caf50" : "#9e9e9e",
                                                        },
                                                    }}
                                                >
                                                    <Avatar
                                                        src={request.from.avatar}
                                                        sx={{ width: 40, height: 40 }}
                                                    >
                                                        {request.from.name.charAt(0)}
                                                    </Avatar>
                                                </Badge>

                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {request.from.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {isOnline
                                                            ? "Online"
                                                            : request.from.lastSeen}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {request.message && (
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: "text.secondary",
                                                        fontStyle: "italic",
                                                        fontSize: "0.8rem",
                                                        lineHeight: 1.3,
                                                        p: 1,
                                                        backgroundColor: alpha("#000", 0.02),
                                                        borderRadius: 1,
                                                        border: "1px solid",
                                                        borderColor: alpha("#000", 0.05),
                                                    }}
                                                >
                                                    "{request.message}"
                                                </Typography>
                                            )}

                                            {/* ✅ Show different buttons based on who initiated the request */}
                                            {request.isRequestedByMe ? (
                                                // Outgoing request - show Pending status and Cancel button
                                                <>
                                                    <Chip
                                                        label="Pending"
                                                        color="warning"
                                                        size="small"
                                                        sx={{ fontSize: "0.75rem" }}
                                                    />
                                                    <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                                                        <Button
                                                            variant="outlined"
                                                            color="error"
                                                            startIcon={<CloseIcon />}
                                                            onClick={() => wrappedCancelRequest(request.id)}
                                                            disabled={processingRequests.has(request.id)}
                                                            sx={{ width: "100%", fontSize: "0.8rem" }}
                                                        >
                                                            {processingRequests.has(request.id) ? 'Canceling...' : 'Cancel Request'}
                                                        </Button>
                                                    </Box>
                                                </>
                                            ) : (
                                                // Incoming request - show Accept and Decline buttons
                                                <>
                                                    <Box sx={{ display: "flex", gap: 1 }}>
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            startIcon={<CheckIcon />}
                                                            onClick={() => wrappedAcceptRequest(request.id)}
                                                            disabled={processingRequests.has(request.id)}
                                                            sx={{ width: "100%", fontSize: "0.8rem" }}
                                                        >
                                                            {processingRequests.has(request.id) ? 'Accepting...' : 'Accept'}
                                                        </Button>
                                                    </Box>

                                                    <Box sx={{ display: "flex", gap: 1 }}>
                                                        <Button
                                                            variant="outlined"
                                                            color="error"
                                                            startIcon={<CloseIcon />}
                                                            onClick={() => wrappedRejectRequest(request.id)}
                                                            disabled={processingRequests.has(request.id)}
                                                            sx={{ width: "100%", fontSize: "0.8rem" }}
                                                        >
                                                            {processingRequests.has(request.id) ? 'Declining...' : 'Decline'}
                                                        </Button>
                                                    </Box>
                                                </>
                                            )}
                                        </Box>
                                    </Paper>
                                )
                            })}
                        </Box>
                    )}
                </Box>
            )}

            {/* Context Menu */}
            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={closeMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <MenuItem
                    onClick={requestRemoveConnection}
                    sx={{ color: "error.main" }}
                >
                    <DeleteOutlineIcon fontSize="small" style={{ marginRight: 8 }} />
                    Remove connection
                </MenuItem>
            </Menu>

            {/* Confirm Remove Dialog */}
            <ParticipantDialogFrame open={confirmOpen} onClose={() => !removing && setConfirmOpen(false)} title="Remove connection?" busy={removing} compact>
                <DialogContent>
                    <DialogContentText>
                        {`This will remove ${menuConnection?.user?.name ?? "this user"} from your connections. You can re-request later.`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} disabled={removing}>Cancel</Button>
                    <Button onClick={handleConfirmRemove} color="error" variant="contained" disabled={removing}>
                        {removing ? "Removing..." : "Remove"}
                    </Button>
                </DialogActions>
            </ParticipantDialogFrame>
        </Box>
    );
};

export default React.memo(ChatConnections);
